import React, { useEffect, useState } from 'react'
import { Autocomplete as MuiAutocomplete, AutocompleteRenderGroupParams, CircularProgress } from '@mui/material'
import { J1DownarrowD } from '@e3dc-react/icons'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useFilteredCompanyOptions } from 'src/Libs/service-dashboard/hooks/use-filtered-company-options'
import { CompanyOption } from 'src/Libs/service-dashboard/models'
import { TextField } from '@e3dc-react/shell/Elements/InputFields'
import { makeStyles } from 'tss-react/mui'

const useStyles = makeStyles()(theme => ({
  searchField: {
    padding: 0,
  },
  input: {
    '.MuiInputBase-root': {
      paddingTop: '0 !important',
      paddingBottom: '0 !important',

      '& .Mui-disabled': {
        outlineColor: theme.palette.grey.inputBorderDisabled,
      },
    },
  },
}))

type Props = {
  name: string
  onValueChange?: (value: CompanyOption | null) => void
  isOptionEqualToValue?: (option: CompanyOption, value: CompanyOption) => boolean
  initialSearchValue?: string
  maxDisplayedResultCount?: number
  renderOption?: (props: React.HTMLAttributes<HTMLLIElement>, option: CompanyOption) => React.ReactNode
  renderGroup?: (params: AutocompleteRenderGroupParams) => React.ReactNode
  groupBy?: (option: CompanyOption) => string
  disabled?: boolean
}

/**
 * Input field for company names that asynchronously fetches data filtered by the input.
 * RHF-variant of service-dashboard/components/CompanySearch.
 */
const CompanySearch: React.FC<Props> = ({
  name,
  onValueChange,
  isOptionEqualToValue,
  initialSearchValue,
  maxDisplayedResultCount,
  renderOption,
  renderGroup,
  groupBy,
  disabled,
}) => {
  const { control, formState } = useFormContext()
  const { t } = useCustomTranslation()
  const { fetchFilteredCompanies } = useFilteredCompanyOptions()
  const { classes } = useStyles()
  const fieldNumberValue = useWatch({ control, name }) as number | null | undefined
  const initialFieldValue = formState.defaultValues?.[name]
  const [options, setOptions] = useState<CompanyOption[]>([])
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [searchValue, setSearchValue] = useState<string | null>(null)
  const [fieldValue, setFieldValue] = useState<CompanyOption | null>(null)

  const loadOptions = async (value: string): Promise<void> => {
    if (!value || value.length < 3 || initialFieldValue === value) {
      setIsMenuOpen(false)
      return
    }

    setLoading(true)

    try {
      const filtered = await fetchFilteredCompanies(value, maxDisplayedResultCount)
      setOptions(filtered)
    } catch {
      setIsMenuOpen(false)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (initialSearchValue && !searchValue) {
      setSearchValue(initialSearchValue)
      loadOptions(initialSearchValue)
    }
  }, [initialSearchValue]) //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (initialSearchValue === searchValue) {
      setFieldValue(options.find(option => option.value === fieldNumberValue) ?? null)
      setIsMenuOpen(false)
    }

    if (searchValue && searchValue !== initialSearchValue && options.length > 0) {
      setIsMenuOpen(true)
    } else {
      setIsMenuOpen(false)
    }
  }, [options]) //eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (event: React.SyntheticEvent | null, value: string): void => {
    if (event?.type === 'click' && value !== '') return
    setSearchValue(value)
    loadOptions(value)
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const handleChange = (value: CompanyOption | null): void => {
          field.onChange(value?.value ?? null)
          onValueChange?.(value)
          setFieldValue(value)
          setIsMenuOpen(false)
        }

        return (
          <MuiAutocomplete<CompanyOption>
            className={classes.searchField}
            classes={{ paper: classes.searchField }}
            value={fieldValue}
            open={isMenuOpen}
            openOnFocus={false}
            onClose={() => setIsMenuOpen(false)}
            options={options}
            loading={loading}
            disabled={disabled}
            filterOptions={x => x}
            popupIcon={<J1DownarrowD />}
            groupBy={groupBy}
            renderGroup={renderGroup}
            renderOption={renderOption}
            getOptionLabel={option => option.optionLabel}
            isOptionEqualToValue={isOptionEqualToValue ?? ((option, value) => option.value === value.value)}
            onInputChange={handleInputChange}
            onChange={(_event, value) => handleChange(value)}
            sx={{ width: '100%' }}
            renderInput={params => (
              <TextField
                {...params}
                name={name}
                label={t('common.companyName')}
                error={!!error}
                helperText={error?.message}
                onBlur={field.onBlur}
                className={classes.input}
                useTooltipErrorMessage={false}
                endAdornment={
                  <>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                }
              />
            )}
          />
        )
      }}
    />
  )
}

CompanySearch.displayName = 'CompanySearch'

export default CompanySearch
